import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import VerticalSplitOutlinedIcon from '@mui/icons-material/VerticalSplitOutlined';
import { v4 as uuid } from 'uuid';
import type { KpiMetric, Newsletter, SectionKey } from '@/types';
import { getEnabledSections, SECTION_LABELS, SECTION_ORDER } from '@/types';
import { generateRandomSections } from '@/utils/randomContent';
import SectionSelectionDialog from '@/components/newsletter/SectionSelectionDialog';
import NewsletterRenderer from '@/components/newsletter/NewsletterRenderer';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  createNewsletterWithCarryForward,
  fetchNewsletters,
  publishNewsletterThunk,
} from '@/store/slices/newslettersSlice';
import { snapshotVersionThunk } from '@/store/slices/versionsSlice';
import {
  createActionItemThunk,
  deleteActionItemThunk,
  fetchActionItems,
  setActionItemStatusThunk,
  updateActionItemThunk,
} from '@/store/slices/actionItemsSlice';
import { showSnackbar } from '@/store/slices/uiSlice';
import { useAutosave } from '@/hooks/useAutosave';
import { MarkdownFieldEditor } from '@/components/editor/Editor';
import ActionItemsPanel from '@/components/actionItems/ActionItems';
import ExportMenu from '@/components/export/ExportMenu';
import { LoadingState } from '@/components/common/Common';
import { ROUTES } from '@/constants/routes';

export default function NewsletterEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: newsletters, status } = useAppSelector((state) => state.newsletters);
  const actionItems = useAppSelector((state) => state.actionItems.items);
  const autosaveStatus = useAppSelector((state) => state.ui.autosaveStatus);

  const [draft, setDraft] = useState<Newsletter | null>(null);
  const [tab, setTab] = useState<'content' | 'actionItems'>('content');
  const [expandedSection, setExpandedSection] = useState<string | false>(SECTION_ORDER[0]);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [splitView, setSplitView] = useState(false);

  // /newsletters/new has no :id — create a draft (carrying forward open action items) then redirect.
  useEffect(() => {
    if (!id) {
      dispatch(createNewsletterWithCarryForward())
        .unwrap()
        .then((result) => navigate(ROUTES.newsletterEdit(result.newsletter.id), { replace: true }));
    }
  }, [id, dispatch, navigate]);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchNewsletters());
    dispatch(fetchActionItems());
  }, [dispatch, status]);

  useEffect(() => {
    if (!id) return;
    const found = newsletters.find((n) => n.id === id);
    if (found) setDraft((current) => (current && current.id === found.id ? current : found));
  }, [id, newsletters]);

  const newsletterActionItems = useMemo(
    () => actionItems.filter((item) => item.newsletterId === id),
    [actionItems, id],
  );

  useAutosave(draft, Boolean(draft));

  if (!id || !draft) return <LoadingState label="Preparing newsletter…" />;

  const enabledSections = getEnabledSections(draft);
  const isSectionEnabled = (key: SectionKey) => enabledSections.includes(key);

  const updateField = <K extends keyof Newsletter>(key: K, value: Newsletter[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const updateSection = (section: keyof Newsletter['sections'], value: string) => {
    setDraft((current) => (current ? { ...current, sections: { ...current.sections, [section]: value } } : current));
  };

  const updateKpi = (kpiId: string, patch: Partial<KpiMetric>) => {
    setDraft((current) =>
      current
        ? { ...current, kpis: current.kpis.map((kpi) => (kpi.id === kpiId ? { ...kpi, ...patch } : kpi)) }
        : current,
    );
  };

  const addKpi = () => {
    const newKpi: KpiMetric = { id: uuid(), label: '', value: '' };
    setDraft((current) => (current ? { ...current, kpis: [...current.kpis, newKpi] } : current));
  };

  const removeKpi = (kpiId: string) => {
    setDraft((current) => (current ? { ...current, kpis: current.kpis.filter((k) => k.id !== kpiId) } : current));
  };

  const handleRandomize = () => {
    updateField('sections', generateRandomSections());
  };

  const handleSaveVersion = async () => {
    await dispatch(
      snapshotVersionThunk({
        newsletter: draft,
        actionItemIds: newsletterActionItems.map((i) => i.id),
        note: 'Manual snapshot',
      }),
    ).unwrap();
    dispatch(showSnackbar({ message: 'Version snapshot saved', severity: 'success' }));
  };

  const handlePublish = async () => {
    const result = await dispatch(
      publishNewsletterThunk({
        newsletter: draft,
        actionItemIds: newsletterActionItems.map((i) => i.id),
      }),
    ).unwrap();
    setDraft(result.newsletter);
    dispatch(showSnackbar({ message: 'Newsletter published', severity: 'success' }));
  };

  const autosaveLabel =
    autosaveStatus === 'saving' ? 'Saving…' : autosaveStatus === 'saved' ? 'All changes saved' : autosaveStatus === 'error' ? 'Save failed' : '';

  return (
    <Stack spacing={2.5}>
      <Box>
        <TextField
          value={draft.title}
          onChange={(e) => updateField('title', e.target.value)}
          variant="standard"
          fullWidth
          InputProps={{ sx: { fontSize: '1.75rem', fontWeight: 700 } }}
        />
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={draft.status === 'published' ? 'Published' : 'Draft'} color={draft.status === 'published' ? 'success' : 'default'} />
        </Stack>
      </Box>

      <Box
        className="no-print"
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: (t) => t.zIndex.appBar - 1,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          px: 1.5,
          py: 1,
        }}
      >
        <TextField
          label="Period start"
          type="date"
          size="small"
          value={draft.periodStart}
          onChange={(e) => updateField('periodStart', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          label="Period end"
          type="date"
          size="small"
          value={draft.periodEnd}
          onChange={(e) => updateField('periodEnd', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
        <Button size="small" startIcon={<VisibilityOutlinedIcon fontSize="small" />} onClick={() => navigate(ROUTES.newsletterPreview(draft.id))}>
          Preview
        </Button>
        <Button
          size="small"
          variant={splitView ? 'contained' : 'text'}
          startIcon={<VerticalSplitOutlinedIcon fontSize="small" />}
          onClick={() => setSplitView((v) => !v)}
          aria-pressed={splitView}
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
        >
          Split View
        </Button>
        <Button size="small" startIcon={<HistoryIcon fontSize="small" />} onClick={() => navigate(ROUTES.newsletterVersions(draft.id))}>
          History
        </Button>
        <Button size="small" startIcon={<ViewAgendaOutlinedIcon fontSize="small" />} onClick={() => setSectionDialogOpen(true)}>
          Sections
        </Button>
        <Button size="small" startIcon={<ShuffleIcon fontSize="small" />} onClick={handleRandomize}>
          Randomize
        </Button>
        <Button size="small" startIcon={<SaveOutlinedIcon fontSize="small" />} onClick={handleSaveVersion}>
          Save Version
        </Button>
        <ExportMenu newsletter={draft} actionItems={newsletterActionItems} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {autosaveLabel}
        </Typography>
        {draft.status === 'draft' && (
          <Button size="small" variant="contained" startIcon={<PublishOutlinedIcon fontSize="small" />} onClick={handlePublish}>
            Publish
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: splitView ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab value="content" label="Content" />
            <Tab value="actionItems" label={`Action Items (${newsletterActionItems.length})`} />
          </Tabs>

          {tab === 'content' ? (
            <Stack spacing={3}>
              {!isSectionEnabled('kpis') && (
                <Typography variant="body2" color="text.secondary">
                  Metrics &amp; KPIs is hidden from this issue's output. Enable it under "Sections" to show it again.
                </Typography>
              )}
              <Box sx={{ display: isSectionEnabled('kpis') ? 'block' : 'none' }}>
                <Typography variant="h3" sx={{ mb: 1 }}>
                  Metrics &amp; KPIs
                </Typography>
                <Grid container spacing={2}>
                  {draft.kpis.map((kpi) => (
                    <Grid item xs={12} sm={6} md={4} key={kpi.id}>
                      <Stack spacing={1} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Stack direction="row" justifyContent="space-between">
                          <TextField
                            label="Label (e.g. MRR)"
                            size="small"
                            value={kpi.label}
                            onChange={(e) => updateKpi(kpi.id, { label: e.target.value })}
                            fullWidth
                          />
                          <IconButton size="small" onClick={() => removeKpi(kpi.id)} aria-label="Remove KPI">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <TextField
                          label="Value (e.g. $412K)"
                          size="small"
                          value={kpi.value}
                          onChange={(e) => updateKpi(kpi.id, { value: e.target.value })}
                        />
                        <TextField
                          label="Target (optional)"
                          size="small"
                          value={kpi.target ?? ''}
                          onChange={(e) => updateKpi(kpi.id, { target: e.target.value })}
                        />
                      </Stack>
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={4}>
                    <Button startIcon={<AddIcon />} onClick={addKpi} sx={{ height: '100%', width: '100%' }}>
                      Add KPI
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                {SECTION_ORDER.filter(isSectionEnabled).map((section) => (
                  <Accordion
                    key={section}
                    expanded={expandedSection === section}
                    onChange={(_, isExpanded) => setExpandedSection(isExpanded ? section : false)}
                    disableGutters
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h4">{SECTION_LABELS[section]}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <MarkdownFieldEditor
                        label={SECTION_LABELS[section]}
                        value={draft.sections[section]}
                        onChange={(value) => updateSection(section, value)}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
                {SECTION_ORDER.filter((s) => !isSectionEnabled(s)).length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {SECTION_ORDER.filter((s) => !isSectionEnabled(s)).length} section(s) hidden from output. Manage
                    under "Sections".
                  </Typography>
                )}
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2}>
              {!isSectionEnabled('actionItems') && (
                <Typography variant="body2" color="text.secondary">
                  Action Items is hidden from this issue's output, but items below are still tracked normally.
                </Typography>
              )}
              <ActionItemsPanel
                newsletterId={draft.id}
                items={newsletterActionItems}
                onCreate={(input) => dispatch(createActionItemThunk(input))}
                onUpdate={(item) => dispatch(updateActionItemThunk(item))}
                onSetStatus={(item, statusValue) => dispatch(setActionItemStatusThunk({ item, status: statusValue }))}
                onDelete={(item) => dispatch(deleteActionItemThunk(item.id))}
              />
            </Stack>
          )}
        </Box>

        {splitView && (
          // top/maxHeight offsets match the app bar + sticky action-bar height above this grid
          <Box
            className="no-print"
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'sticky',
              top: { md: 132 },
              maxHeight: 'calc(100vh - 148px)',
              overflowY: 'auto',
              minWidth: 0,
            }}
          >
            <NewsletterRenderer newsletter={draft} actionItems={newsletterActionItems} />
          </Box>
        )}
      </Box>

      <SectionSelectionDialog
        open={sectionDialogOpen}
        enabledSections={enabledSections}
        onClose={() => setSectionDialogOpen(false)}
        onSave={(sections) => updateField('enabledSections', sections)}
      />
    </Stack>
  );
}
